import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import authService from '../services/authService';
import { apiService } from '../services/apiService';

interface ImpersonationBarProps {
  /** Increment app session key so screens refetch with the new user */
  onSessionChanged?: () => void;
}

interface ImpersonationUser {
  id: string;
  email: string;
  role: string;
  companyName?: string | null;
}

interface UserListHeaderItem {
  type: 'header';
  key: string;
  letter: string;
}

interface UserListUserItem {
  type: 'user';
  key: string;
  user: ImpersonationUser;
}

type UserListItem = UserListHeaderItem | UserListUserItem;

export const ImpersonationBar: React.FC<ImpersonationBarProps> = ({ onSessionChanged }) => {
  const [chrome, setChrome] = useState(authService.getImpersonationChromeState());
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<ImpersonationUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshChrome = useCallback(() => {
    setChrome(authService.getImpersonationChromeState());
  }, []);

  useEffect(() => {
    refreshChrome();
  }, [refreshChrome]);

  const loadUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      setError(null);
      const list = await apiService.getUsers();
      setUsers(
        list.map((u) => ({
          id: u.id,
          email: u.email,
          role: u.role,
          companyName: u.companyName ?? null,
        })),
      );
    } catch (e: any) {
      setError(e?.message || 'Could not load users');
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (modalOpen) {
      loadUsers();
    }
  }, [modalOpen, loadUsers]);

  const filteredUsers = useMemo(() => {
    const effectiveId = authService.parseAccessTokenPayload()?.userId;
    const base = users.filter(
      u => u.role !== 'super_admin' && (!effectiveId || u.id !== effectiveId)
    );
    const sorted = [...base].sort((a, b) => {
      const companyA = (a.companyName || '').trim().toLowerCase();
      const companyB = (b.companyName || '').trim().toLowerCase();
      const companyCmp = companyA.localeCompare(companyB);
      if (companyCmp !== 0) return companyCmp;
      return a.email.toLowerCase().localeCompare(b.email.toLowerCase());
    });
    const q = search.trim().toLowerCase();
    if (!q) {
      return sorted;
    }
    return sorted.filter((u) => {
      const company = (u.companyName || '').toLowerCase();
      return u.email.toLowerCase().includes(q) || company.includes(q);
    });
  }, [users, search]);

  const usersWithHeaders = useMemo<UserListItem[]>(() => {
    const items: UserListItem[] = [];
    let lastLetter = '';

    filteredUsers.forEach((user) => {
      const sortLabel = (user.companyName || user.email || '').trim();
      const firstChar = sortLabel.charAt(0).toUpperCase();
      const letter = /^[A-Z]$/.test(firstChar) ? firstChar : '#';

      if (letter !== lastLetter) {
        items.push({
          type: 'header',
          key: `header-${letter}`,
          letter,
        });
        lastLetter = letter;
      }

      items.push({
        type: 'user',
        key: user.id,
        user,
      });
    });

    return items;
  }, [filteredUsers]);

  const handleImpersonate = async (targetUserId: string) => {
    try {
      setBusy(true);
      setError(null);
      await authService.impersonateAs(targetUserId);
      setModalOpen(false);
      setSearch('');
      refreshChrome();
      onSessionChanged?.();
    } catch (e: any) {
      setError(e?.message || 'Could not start impersonation');
    } finally {
      setBusy(false);
    }
  };

  const handleStop = async () => {
    try {
      setBusy(true);
      setError(null);
      await authService.stopImpersonation();
      refreshChrome();
      onSessionChanged?.();
    } catch (e: any) {
      setError(e?.message || 'Could not restore your session');
    } finally {
      setBusy(false);
    }
  };

  if (!chrome.showChrome) {
    return null;
  }

  const payload = authService.parseAccessTokenPayload();

  return (
    <>
      <SafeAreaView edges={['top']} style={styles.safe}>
        <View style={styles.bar}>
          {chrome.isImpersonating && (
            <View style={styles.row}>
              <Text style={styles.impersonationLabel} numberOfLines={2}>
                Viewing as:{' '}
                <Text style={styles.impersonationEmail}>{payload?.email || 'user'}</Text>
              </Text>
              <TouchableOpacity
                style={[styles.button, styles.buttonPrimary]}
                onPress={handleStop}
                disabled={busy}
                activeOpacity={0.85}
              >
                {busy ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <Text style={styles.buttonPrimaryText}>Back to my user</Text>
                )}
              </TouchableOpacity>
            </View>
          )}

          {chrome.canOpenUserPicker && (
            <View style={[styles.row, chrome.isImpersonating && styles.rowSecond]}>
              <Text style={styles.superLabel}>
                {chrome.isSuperAdmin ? 'Super administrator' : 'Impersonation active'}
              </Text>
              <TouchableOpacity
                style={[styles.button, styles.buttonOutline]}
                onPress={() => {
                  setModalOpen(true);
                  setError(null);
                }}
                disabled={busy}
                activeOpacity={0.85}
              >
                <Text style={styles.buttonOutlineText}>
                  {chrome.isImpersonating ? 'Switch user…' : 'View as another user…'}
                </Text>
              </TouchableOpacity>
            </View>
          )}

          {error ? <Text style={styles.errorInline}>{error}</Text> : null}
        </View>
      </SafeAreaView>

      <Modal visible={modalOpen} animationType="slide" transparent onRequestClose={() => setModalOpen(false)}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Choose user</Text>
            <TextInput
              style={styles.search}
              placeholder="Search by company or email…"
              placeholderTextColor="#9CA3AF"
              value={search}
              onChangeText={setSearch}
              autoCapitalize="none"
              autoCorrect={false}
              clearButtonMode="while-editing"
            />
            {loadingUsers ? (
              <ActivityIndicator style={styles.modalLoading} />
            ) : (
              <FlatList
                style={styles.list}
                data={usersWithHeaders}
                keyExtractor={item => item.key}
                keyboardShouldPersistTaps="handled"
                renderItem={({ item }) => (
                  item.type === 'header' ? (
                    <View style={styles.letterHeaderRow}>
                      <Text style={styles.letterHeaderText}>{item.letter}</Text>
                    </View>
                  ) : (
                    <TouchableOpacity
                      style={styles.userRow}
                      onPress={() => handleImpersonate(item.user.id)}
                      disabled={busy}
                    >
                      <Text style={styles.userEmail}>
                        {item.user.companyName
                          ? `${item.user.companyName} - ${item.user.email}`
                          : item.user.email}
                      </Text>
                      <Text style={styles.userRole}>{item.user.role}</Text>
                    </TouchableOpacity>
                  )
                )}
                ListEmptyComponent={
                  <Text style={styles.empty}>No matching users</Text>
                }
              />
            )}
            {error ? <Text style={styles.errorModal}>{error}</Text> : null}
            <TouchableOpacity style={styles.closeModal} onPress={() => setModalOpen(false)}>
              <Text style={styles.closeModalText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  safe: {
    backgroundColor: '#FEF3C7',
    zIndex: 50,
    ...Platform.select({
      web: {
        position: 'sticky' as any,
        top: 0,
      },
    }),
  },
  bar: {
    paddingHorizontal: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#FCD34D',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
  },
  rowSecond: {
    marginTop: 10,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(180, 83, 9, 0.25)',
  },
  impersonationLabel: {
    flex: 1,
    minWidth: 160,
    fontSize: 13,
    color: '#78350F',
    fontFamily: 'Montserrat_500Medium',
  },
  impersonationEmail: {
    fontFamily: 'Montserrat_600SemiBold',
    color: '#92400E',
  },
  superLabel: {
    flex: 1,
    fontSize: 13,
    color: '#92400E',
    fontFamily: 'Montserrat_600SemiBold',
  },
  button: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    minWidth: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonPrimary: {
    backgroundColor: '#B45309',
  },
  buttonPrimaryText: {
    color: '#fff',
    fontSize: 13,
    fontFamily: 'Montserrat_600SemiBold',
  },
  buttonOutline: {
    borderWidth: 1,
    borderColor: '#B45309',
    backgroundColor: '#FFFBEB',
  },
  buttonOutlineText: {
    color: '#92400E',
    fontSize: 13,
    fontFamily: 'Montserrat_600SemiBold',
  },
  errorInline: {
    marginTop: 6,
    fontSize: 12,
    color: '#B91C1C',
    fontFamily: 'Montserrat_500Medium',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    padding: 20,
  },
  modalCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    maxHeight: '80%' as any,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: 'Montserrat_600SemiBold',
    color: '#111827',
    marginBottom: 12,
  },
  search: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: Platform.OS === 'ios' ? 12 : 8,
    fontSize: 15,
    marginBottom: 12,
    fontFamily: 'Montserrat_400Regular',
  },
  modalLoading: {
    marginVertical: 24,
  },
  list: {
    maxHeight: 320,
  },
  letterHeaderRow: {
    paddingTop: 10,
    paddingBottom: 4,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#E5E7EB',
    backgroundColor: '#FAFAFA',
  },
  letterHeaderText: {
    fontSize: 13,
    color: '#6B7280',
    fontFamily: 'Montserrat_600SemiBold',
    textTransform: 'uppercase',
  },
  userRow: {
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#E5E7EB',
  },
  userEmail: {
    fontSize: 15,
    color: '#111827',
    fontFamily: 'Montserrat_500Medium',
  },
  userRole: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
    fontFamily: 'Montserrat_400Regular',
    textTransform: 'capitalize',
  },
  empty: {
    textAlign: 'center',
    color: '#9CA3AF',
    marginTop: 16,
    fontFamily: 'Montserrat_400Regular',
  },
  errorModal: {
    marginTop: 8,
    fontSize: 13,
    color: '#B91C1C',
    fontFamily: 'Montserrat_500Medium',
  },
  closeModal: {
    marginTop: 16,
    alignSelf: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  closeModalText: {
    fontSize: 15,
    color: '#00506E',
    fontFamily: 'Montserrat_600SemiBold',
  },
});
